/**
 * DS2026 stacked toaster — Sonner-style feedback toasts.
 *
 * Mount once near the app root (see main.tsx). Reads from the module-level
 * toastStore, so anything can push via the imperative API. Bottom-right; newest
 * toast in front, older ones recede upward behind it; hover/focus fans the stack
 * out and pauses auto-dismiss. Every toast is a uniform single row, so the
 * collapsed stack stays consistent and the expanded layout is deterministic.
 *
 * Full spec + the "is this an upgrade" assessment: ./DS2026-TOAST.md.
 */
import { useRef, useState, useSyncExternalStore } from 'react'
import type { IconType } from 'react-icons'
import {
  PiCheckCircle,
  PiCircleNotch,
  PiInfo,
  PiPaperPlaneTilt,
  PiWarning,
  PiX,
  PiXCircle,
} from 'react-icons/pi'
import { useMountEffect } from 'hooks/useMountEffect'
import { Z_INDEX } from 'styles/z-index'
import {
  dismissToast,
  getToasts,
  pauseTimers,
  resumeTimers,
  subscribe,
  type ToastData,
  type ToastId,
  type ToastVariant,
} from './toastStore'

// Variant icon + color — AA-checked `-strong` tokens (3:1 non-text on white).
const VARIANT_ICON: Record<
  ToastVariant,
  { Icon: IconType; className: string }
> = {
  success: { Icon: PiCheckCircle, className: 'text-success-strong' },
  error: { Icon: PiXCircle, className: 'text-error-strong' },
  warning: { Icon: PiWarning, className: 'text-warning-strong' },
  info: { Icon: PiInfo, className: 'text-fg-secondary' },
  message: { Icon: PiPaperPlaneTilt, className: 'text-brand-active' },
  loading: { Icon: PiCircleNotch, className: 'text-brand-bright animate-spin' },
}

// Elevated light surface — a stronger shadow than the Figma "shadow-sm" so the
// toast lifts clearly off the very light app background (the chosen treatment;
// see DS2026-TOAST.md "surface").
const TOAST_SHADOW =
  'shadow-[0px_10px_28px_rgba(16,24,40,0.16),0px_3px_8px_rgba(16,24,40,0.08)]'
const TOAST_SHELL = `w-full rounded-lg border-[0.5px] border-line bg-surface ${TOAST_SHADOW}`

const VISIBLE_LIMIT = 3 // collapsed toasts shown before the rest hide
const PEEK = 13 // px each older collapsed toast peeks above the front one
const SCALE_STEP = 0.05 // each older collapsed toast shrinks by this much
const GAP = 10 // px between toasts when expanded
const ROW_H = 33 // first-paint fallback only; real heights come from ResizeObserver
const COLLAPSE_DELAY = 220 // ms grace before re-collapsing on mouse-leave

/**
 * Measure each toast's real rendered height via a ResizeObserver, keyed by id.
 * The expanded stack uses these so multi-line toasts space correctly (a fixed
 * row constant would overlap them). Returns the height map plus a stable
 * per-id ref callback to attach to each toast wrapper. `ROW_H` is only the
 * first-paint fallback before a toast has been measured.
 */
function useToastHeights() {
  const [heights, setHeights] = useState<Record<ToastId, number>>({})
  const roRef = useRef<ResizeObserver | null>(null)
  const elToId = useRef(new Map<Element, ToastId>())
  const refCbs = useRef(new Map<ToastId, (el: HTMLDivElement | null) => void>())

  if (roRef.current === null && typeof ResizeObserver !== 'undefined') {
    roRef.current = new ResizeObserver((entries) => {
      setHeights((prev) => {
        let next = prev
        for (const entry of entries) {
          const id = elToId.current.get(entry.target)
          if (id === undefined) continue
          const h = (entry.target as HTMLElement).offsetHeight
          if (h && next[id] !== h) {
            if (next === prev) next = { ...prev }
            next[id] = h
          }
        }
        return next
      })
    })
  }

  // Root-mounted in production, but disconnect anyway so test renders are clean.
  useMountEffect(() => () => roRef.current?.disconnect())

  // Stable callback per id, so a re-render doesn't detach/re-observe every toast.
  const measureRef = (id: ToastId) => {
    let cb = refCbs.current.get(id)
    if (!cb) {
      cb = (el: HTMLDivElement | null) => {
        const ro = roRef.current
        if (el) {
          elToId.current.set(el, id)
          ro?.observe(el)
        } else {
          for (const [node, nodeId] of elToId.current) {
            if (nodeId !== id) continue
            ro?.unobserve(node)
            elToId.current.delete(node)
            break
          }
          refCbs.current.delete(id)
        }
      }
      refCbs.current.set(id, cb)
    }
    return cb
  }

  return { heights, measureRef }
}

function Toast({
  toast,
  onDismiss,
}: {
  toast: ToastData
  onDismiss: () => void
}) {
  const { Icon, className } = VARIANT_ICON[toast.variant]
  // Content may be a render function (react-toastify compatibility) that needs
  // `closeToast` injected — e.g. a toast with its own inline Undo button.
  const content =
    typeof toast.title === 'function'
      ? toast.title({ closeToast: onDismiss })
      : toast.title
  return (
    // items-start so a wrapping (multi-line) message keeps the icon and the
    // controls pinned to the first line instead of centered on the whole block.
    // The close button's vertical padding is neutralized (-my-1.5) so it doesn't
    // make a single-line row taller than the text — keeping that case balanced.
    <div className={`${TOAST_SHELL} flex items-start gap-2.5 py-2 pl-3 pr-2`}>
      <Icon className={`mt-px size-5 shrink-0 ${className}`} />
      <div className="flex-1 text-sm font-medium tracking-[-0.2px] text-fg">
        {content}
      </div>
      {toast.action && (
        <button
          type="button"
          onClick={() => {
            toast.action?.onClick?.()
            onDismiss()
          }}
          className="shrink-0 px-1 text-sm font-medium text-brand-active hover:text-brand"
        >
          {toast.action.label}
        </button>
      )}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-my-1.5 shrink-0 rounded-md p-1.5 text-fg-tertiary transition-colors hover:bg-surface-sunken hover:text-fg-secondary"
      >
        <PiX className="size-5" />
      </button>
    </div>
  )
}

export function Toaster() {
  const toasts = useSyncExternalStore(subscribe, getToasts)
  const [expanded, setExpanded] = useState(false)
  const collapseTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { heights, measureRef } = useToastHeights()

  // Cancel any pending collapse on unmount so it can't fire setState after the
  // component is gone (matters for test renders; root-mounted in production).
  useMountEffect(() => () => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current)
  })

  // Newest (last added) renders in front, at index 0.
  const ordered = [...toasts].reverse()
  const overflow = ordered.length - VISIBLE_LIMIT
  // Front toast's measured height — anchors the overflow pill above it (the
  // container's height equals this, so a fixed constant would let a multi-line
  // front toast overlap the pill).
  const front = ordered[0]
  const frontHeight = front ? (heights[front.id] ?? ROW_H) : ROW_H

  // Expanded lift for toast i = sum of the real heights of the toasts in front
  // of it (+ gaps). Falls back to ROW_H for any toast not yet measured.
  const liftFor = (index: number) => {
    let sum = 0
    for (let j = 0; j < index; j++)
      sum += (heights[ordered[j].id] ?? ROW_H) + GAP
    return sum
  }

  // Entering pauses auto-dismiss (so you can read what you point at) + expands;
  // leaving resumes and re-collapses after a short anti-jitter grace.
  const onEnter = () => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current)
    pauseTimers()
    setExpanded(true)
  }
  const onLeave = () => {
    if (collapseTimer.current) clearTimeout(collapseTimer.current)
    resumeTimers()
    collapseTimer.current = setTimeout(() => setExpanded(false), COLLAPSE_DELAY)
  }
  // Only collapse when focus actually leaves the stack — not on tabbing between
  // toasts (intra-container focus moves would otherwise schedule a re-collapse).
  const onBlur = (e: React.FocusEvent) => {
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onLeave()
  }

  if (toasts.length === 0) return null

  // Slide/scale only when reduced motion isn't requested; the spinner keeps
  // spinning since it signals activity.
  const motion =
    'origin-bottom motion-safe:transition-all motion-safe:[transition-duration:450ms] motion-safe:[transition-timing-function:cubic-bezier(0.22,1,0.36,1)]'

  return (
    <div
      className="pointer-events-none fixed bottom-4 right-4 w-[385px]"
      style={{ zIndex: Z_INDEX.TOAST }}
      aria-live="polite"
      role="region"
      aria-label="Notifications"
    >
      {/* Hover/focus only toggles a visual affordance (collapse ⇄ expand); each
          toast stays individually operable, so this grouping needs no role. */}
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div
        className="pointer-events-auto relative"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={onEnter}
        onBlur={onBlur}
      >
        {!expanded && overflow > 0 && (
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-center"
            style={{
              transform: `translateY(${-(frontHeight + 24)}px)`,
              zIndex: ordered.length + 1,
            }}
          >
            <span className="rounded-full border-[0.5px] border-line bg-surface px-2 py-0.5 text-xxs font-medium text-fg-secondary shadow-sm">
              +{overflow} more
            </span>
          </div>
        )}

        {ordered.map((t, i) => {
          const isFront = i === 0
          const hidden = !expanded && i >= VISIBLE_LIMIT

          // The newest toast sits in normal flow and gives the container a real
          // height to anchor against. Older toasts stack absolutely *above* it.
          if (isFront) {
            return (
              <div
                key={t.id}
                ref={measureRef(t.id)}
                className={`relative ${motion}`}
                style={{ zIndex: ordered.length }}
              >
                <Toast toast={t} onDismiss={() => dismissToast(t.id)} />
              </div>
            )
          }

          // Collapsed = small fixed peek + scale-down; expanded = lifted by the
          // measured heights of the toasts in front of it (handles multi-line).
          const lift = expanded ? liftFor(i) : i * PEEK
          const scale = expanded ? 1 : 1 - i * SCALE_STEP
          return (
            <div
              key={t.id}
              ref={measureRef(t.id)}
              className={`absolute inset-x-0 bottom-0 ${motion}`}
              style={{
                transform: `translateY(${-lift}px) scale(${scale})`,
                zIndex: ordered.length - i,
                opacity: hidden ? 0 : 1,
                pointerEvents: hidden ? 'none' : 'auto',
              }}
            >
              <Toast toast={t} onDismiss={() => dismissToast(t.id)} />
            </div>
          )
        })}
      </div>
    </div>
  )
}
